
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot } from '@angular/router';
import { ApiError, SessionInfo } from 'app/backend/api';
import { Rest } from 'app/backend/rest';
import { environment } from 'environment';
import * as JWT from 'jwt-decode';
import { Observable, of as observableOf } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

const LS_TOKEN = 'token';
const LS_SCOPE = 'scope';
const LS_SESSION = 'session';
const LS_PREV_LOGIN = 'previousLogin';
const LS_DISPLAY_NAME = 'displayName';
const LS_OBJECT_ID = 'objectId';
const LS_ROLE_ID = 'roleId';
const LS_COMPANY_ID = 'companyId';

@Injectable()
export class AuthGuard implements CanActivate, CanActivateChild {

  public session: string = localStorage.getItem(LS_SESSION);
  public token: string = localStorage.getItem(LS_TOKEN);
  public authenticated = false;

  constructor(
    private router: Router,
    private backend: Rest
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.checkLogin(state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    const companyId: string = localStorage.getItem(LS_COMPANY_ID);
    const token: string = localStorage.getItem(LS_TOKEN);
    const decodedToken: any = token && JWT(token);
    const currentRole: string = decodedToken && decodedToken.role;

    const permissions = route.data.roles;

    const available: any[] = permissions && permissions[currentRole];

    if (environment.name === 'production' && (state.url === '/explorer' || state.url === '/playground')) {
      const redirect = decodedToken.sub === 'user:internal' ? '/nederman/companies' : '/customer/companies/' + companyId;
      this.router.navigate([redirect]);
      return observableOf(false);
    }

    if (!available || !available.length) {
      const redirect = decodedToken.sub === 'user:internal' ? '/nederman/companies' : '/customer/companies/' + companyId;
      this.router.navigate([redirect]);
      return observableOf(false);
    }

    const pass = available.find(properties => {
      return state.url.match(new RegExp(properties.regex, 'i')) ? true : false;
    });

    if (pass) {
      return this.canActivate(route, state);
    } else {
      const firstAvalible = available[0];
      const checkParam = /\{(.*?)\}/;
      const param = checkParam.exec(firstAvalible.url);
      let redirect = firstAvalible.url;
      if (param) {
        redirect = redirect.replace(checkParam, localStorage.getItem(firstAvalible[param[1]]));
      }
      this.router.navigate([redirect]);
      return observableOf(false);
    }
  }

  checkLogin(state: RouterStateSnapshot): Observable<boolean> {
    return this.isAuthenticated().pipe(
      map(auth => {
        if (!auth) {
          this.router.navigate(['/auth'], { queryParams: { returnUrl: state.url } });
        }
        return auth;
      }));
  }

  isAuthenticated(): Observable<boolean> {
    if (this.authenticated) {
      return observableOf(true);
    } else if (this.session && this.token) {
      return this.backend.getBackend('session/' + this.session).pipe(
        map((res: SessionInfo) => {
          localStorage.setItem(LS_DISPLAY_NAME, res.name);
          localStorage.setItem(LS_SCOPE, res.scope);
          localStorage.setItem(LS_OBJECT_ID, res.userId);
          localStorage.setItem(LS_ROLE_ID, res.role);
          localStorage.setItem(LS_COMPANY_ID, res.companyId);
          if (res.previousLogin) {
            localStorage.setItem(LS_PREV_LOGIN, 'true');
          } else {
            localStorage.removeItem(LS_PREV_LOGIN);
          }
          this.authenticated = true;
          return true;
        }), catchError((err: ApiError) => {
          this.clearSessionToken();
          return observableOf(false);
        }));
    } else {
      return observableOf(false);
    }
  }

  private redirect() {

  }

  private clearSessionToken() {
    localStorage.removeItem(LS_SESSION);
    localStorage.removeItem(LS_TOKEN);
    localStorage.removeItem(LS_PREV_LOGIN);
    this.session = null;
    this.token = null;
    this.authenticated = false;
  }
}
